import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  BATCH_SITUATIONS,
  BATCH_SITUATION_LABELS,
  formatCents,
  formatQty,
  type BatchSituation,
  type Paginated,
  type StockBatch,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { errorMessage } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useStockLocations } from '@/lib/queries';
import { downloadCsv } from '@/lib/csv';

const TONE: Record<BatchSituation, BadgeKind> = {
  VENCIDO: 'bad',
  VENCE_EM_BREVE: 'warn',
  NO_PRAZO: 'ok',
  SEM_VALIDADE: 'mute',
};

/** "vencido há 3 dias" / "vence em 12 dias" — o número sozinho não diz nada. */
function prazo(dias: number | null): string {
  if (dias === null) return '—';
  if (dias < 0) return `vencido há ${Math.abs(dias)} ${Math.abs(dias) === 1 ? 'dia' : 'dias'}`;
  if (dias === 0) return 'vence hoje';
  return `vence em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/**
 * Validades — a tela que existe para o material não estragar na prateleira.
 *
 * A ordem é sempre a mesma: o que vence primeiro em cima. Quem abre esta tela
 * está perguntando "o que eu preciso usar ou jogar fora esta semana", e a
 * resposta tem de estar na primeira linha, não na terceira página.
 */
export function ValidadesPage() {
  const locations = useStockLocations();

  const [search, setSearch] = useState('');
  const [locationId, setLocationId] = useState('');
  const [situation, setSituation] = useState('');
  const [page, setPage] = useState(1);

  const listQuery = useQuery({
    queryKey: ['stock-batches', { search, locationId, situation, page }],
    queryFn: () =>
      api.get<Paginated<StockBatch>>('/stock-batches', {
        search: search || undefined,
        locationId: locationId || undefined,
        situation: situation || undefined,
        page,
        perPage: 50,
      }),
  });

  const alertQuery = useQuery({
    queryKey: ['stock-batches', 'alert'],
    queryFn: () => api.get<{ expired: number; expiring: number }>('/stock-batches/alert'),
  });

  const rows = listQuery.data?.data ?? [];

  const columns: Array<Column<StockBatch>> = [
    { key: 'code', label: 'Lote', mono: true, strong: true, render: (row) => row.code },
    {
      key: 'item',
      label: 'Item',
      render: (row) => (
        <>
          <span className="mono" style={{ color: T.inkFaint }}>
            {row.sku}
          </span>{' '}
          {row.itemName}
        </>
      ),
    },
    { key: 'locationName', label: 'Local', render: (row) => row.locationName },
    {
      key: 'quantityMilli',
      label: 'Saldo',
      right: true,
      mono: true,
      render: (row) => `${formatQty(row.quantityMilli)} ${row.unit}`,
    },
    {
      key: 'expiryDate',
      label: 'Validade',
      mono: true,
      render: (row) => (row.expiryDate ? formatDate(row.expiryDate) : '—'),
    },
    {
      key: 'daysToExpiry',
      label: 'Prazo',
      render: (row) => (
        <span style={{ color: row.situation === 'VENCIDO' ? T.red : T.inkSoft, fontSize: 12.5 }}>
          {prazo(row.daysToExpiry)}
        </span>
      ),
    },
    {
      key: 'totalCostCents',
      label: 'Valor parado',
      right: true,
      mono: true,
      render: (row) => formatCents(row.totalCostCents),
    },
    {
      key: 'situation',
      label: '',
      width: 150,
      render: (row) => (
        <StatusBadge kind={TONE[row.situation]}>{BATCH_SITUATION_LABELS[row.situation]}</StatusBadge>
      ),
    },
  ];

  const alerta = alertQuery.data;
  const hasFilters = Boolean(search || locationId || situation);

  return (
    <>
      <WorkspaceHeader
        title="Validades"
        meta={
          alerta
            ? [
                { label: 'Vencidos', value: alerta.expired, tone: alerta.expired > 0 ? 'bad' : undefined },
                {
                  label: 'Vencem em 30 dias',
                  value: alerta.expiring,
                  tone: alerta.expiring > 0 ? 'warn' : undefined,
                },
              ]
            : undefined
        }
        actions={
          <Button
            icon={Download}
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv('validades-estoque', [
                ['Lote', 'Código', 'Item', 'Local', 'Saldo', 'Unidade', 'Validade', 'Situação', 'Valor'],
                ...rows.map((row) => [
                  row.code,
                  row.sku,
                  row.itemName,
                  row.locationName,
                  formatQty(row.quantityMilli),
                  row.unit,
                  row.expiryDate ? formatDate(row.expiryDate) : '',
                  BATCH_SITUATION_LABELS[row.situation],
                  formatCents(row.totalCostCents),
                ]),
              ])
            }
          >
            Exportar CSV
          </Button>
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por lote, código ou nome do item…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'location',
              label: 'Local',
              value: locationId,
              options: (locations.data ?? []).map((location) => ({
                value: location.id,
                label: `${location.code} · ${location.name}`,
              })),
              onChange: (value) => {
                setLocationId(value);
                setPage(1);
              },
            },
            {
              key: 'situation',
              label: 'Situação',
              value: situation,
              options: BATCH_SITUATIONS.map((value) => ({
                value,
                label: BATCH_SITUATION_LABELS[value],
              })),
              onChange: (value) => {
                setSituation(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Lotes com saldo"
            columns={columns}
            rows={rows}
            meta={listQuery.data.meta}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setLocationId('');
                    setSituation('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum lote em estoque"
                  description="Marque “Controla lote e validade” no cadastro do item; a partir daí toda entrada registra o lote."
                />
              )
            }
          />
        )}
      </div>
    </>
  );
}
